


import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, Share2, Timer, Trash2 } from 'lucide-react';
import TagInput from '../../atoms/TagInput';
import ScheduleSelectionModal from '../ScheduleSelectionModal';
import { GeneralTabProps } from './types';

const GeneralTab: React.FC<GeneralTabProps> = ({ node, handleUpdate, allNodes, getScheduleName }) => {
    const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
    const [isPhaseOutModalOpen, setIsPhaseOutModalOpen] = useState(false);

    // Follow Up Handlers
    const handleToggleFollowUp = (enabled: boolean) => {
        if (enabled) {
            handleUpdate('followUp', ''); // Enable with empty ID
        } else {
            handleUpdate('followUp', null); // Disable
        }
    };

    // Phase Out Handlers
    const handlePhaseOutChange = (field: 'scheduleId' | 'duration', value: string) => {
        if (!node.phaseOut && value) {
            handleUpdate('phaseOut', { scheduleId: value, duration: '30d' }); 
        } else if (node.phaseOut) {
            handleUpdate('phaseOut', { ...node.phaseOut, [field]: value });
        }
    };

    const handleTogglePhaseOut = (enabled: boolean) => {
        if (enabled) {
            handleUpdate('phaseOut', { scheduleId: '', duration: '30d' }); // Initialize empty
        } else {
            handleUpdate('phaseOut', null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Status */}
            <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${node.isActive ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                        {node.isActive ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    </div>
                    <div>
                        <div className="font-semibold text-sm text-slate-900">Schedule Status</div>
                        <div className="text-xs text-slate-500">{node.isActive ? 'Active & Enforced' : 'Inactive & Ignored'}</div>
                    </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={node.isActive} onChange={(e) => handleUpdate('isActive', e.target.checked)} />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Basic Information</h3>
                
                <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Name</label>
                    <input 
                        type="text" 
                        value={node.name} 
                        onChange={(e) => handleUpdate('name', e.target.value)} 
                        className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Group</label>
                        <input 
                            type="text" 
                            value={node.group} 
                            onChange={(e) => handleUpdate('group', e.target.value)} 
                            className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Purpose</label>
                        <input 
                            type="text" 
                            value={node.purpose} 
                            onChange={(e) => handleUpdate('purpose', e.target.value)} 
                            className="w-full p-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Tags <span className="text-[10px] font-normal text-slate-400 opacity-80">(Search)</span></label>
                    <TagInput 
                        tags={node.tags || []} 
                        onChange={(tags) => handleUpdate('tags', tags)}
                    />
                </div>

                <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Attributes <span className="text-[10px] font-normal text-slate-400 opacity-80">(Workflow Logic)</span></label>
                    <TagInput 
                        tags={node.attributes || []} 
                        onChange={(attrs) => handleUpdate('attributes', attrs)}
                        placeholder="Add attribute..."
                        colorClass="bg-indigo-50 text-indigo-700 border-indigo-200"
                    />
                </div>
            </div>

            {/* Lifecycle */}
            <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Lifecycle</h3>
                
                {/* Follow Up */}
                <div className={`p-3 border rounded-lg transition-colors ${node.followUp !== null ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <Share2 size={16} className={node.followUp !== null ? "text-indigo-500" : "text-slate-400"} />
                            <div>
                                <div className="text-sm font-medium text-slate-900">Follow Up Schedule</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer" 
                                checked={node.followUp !== null} 
                                onChange={(e) => handleToggleFollowUp(e.target.checked)} 
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                        </label>
                    </div>
                    {node.followUp !== null && (
                            <div className="mt-3 space-y-3 pl-2 border-l-2 border-indigo-200">
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Target Schedule</label>
                                <div className="flex gap-2">
                                    <div className="flex-1 text-sm p-2 bg-white border border-indigo-200 rounded text-slate-700 flex items-center justify-between">
                                            <span className={!node.followUp ? "text-slate-400 italic" : ""}>
                                            {node.followUp ? getScheduleName(node.followUp) : 'None configured'}
                                            </span>
                                    </div>
                                    <button 
                                        onClick={() => setIsFollowUpModalOpen(true)}
                                        className="px-3 py-1 bg-white border border-indigo-200 text-indigo-700 text-xs rounded hover:bg-indigo-100 transition-colors"
                                    >
                                        Select
                                    </button>
                                    {node.followUp && (
                                        <button 
                                            onClick={() => handleUpdate('followUp', '')}
                                            className="p-2 bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                            title="Remove Follow Up"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Phase Out */}
                <div className={`p-3 border rounded-lg transition-colors ${node.phaseOut ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <Timer size={16} className={node.phaseOut ? "text-amber-500" : "text-slate-400"} />
                            <div>
                                <div className="text-sm font-medium text-slate-900">Phase Out Logic</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer" 
                                checked={!!node.phaseOut} 
                                onChange={(e) => handleTogglePhaseOut(e.target.checked)} 
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                    </div>
                    {node.phaseOut && (
                        <div className="mt-3 space-y-3 pl-2 border-l-2 border-amber-200">
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Replacement Schedule</label>
                                <div className="flex gap-2">
                                    <div className="flex-1 text-sm p-2 bg-white border border-amber-200 rounded text-slate-700 flex items-center justify-between">
                                        <span className={!node.phaseOut.scheduleId ? "text-slate-400 italic" : ""}>
                                            {getScheduleName(node.phaseOut.scheduleId) || 'None configured'}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => setIsPhaseOutModalOpen(true)}
                                        className="px-3 py-1 bg-white border border-amber-200 text-amber-700 text-xs rounded hover:bg-amber-100 transition-colors"
                                    >
                                        Select
                                    </button>
                                    {node.phaseOut.scheduleId && (
                                            <button 
                                            onClick={() => handlePhaseOutChange('scheduleId', '')}
                                            className="p-2 bg-white border border-amber-200 text-amber-700 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                            title="Remove Replacement"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Duration</label>
                                <input 
                                    type="text" 
                                    value={node.phaseOut.duration}
                                    onChange={(e) => handlePhaseOutChange('duration', e.target.value)}
                                    className="w-full p-2 bg-white text-slate-900 border border-amber-200 rounded text-sm focus:outline-none focus:border-amber-400"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ScheduleSelectionModal
                isOpen={isFollowUpModalOpen}
                onClose={() => setIsFollowUpModalOpen(false)}
                schedules={allNodes}
                onSelect={(id) => handleUpdate('followUp', id)}
                title="Select Follow Up Schedule"
                excludeId={node.scheduleId}
            />

            <ScheduleSelectionModal
                isOpen={isPhaseOutModalOpen}
                onClose={() => setIsPhaseOutModalOpen(false)}
                schedules={allNodes}
                onSelect={(id) => handlePhaseOutChange('scheduleId', id)}
                title="Select Replacement Schedule"
                excludeId={node.scheduleId}
            />
        </div>
    );
};

export default GeneralTab;